import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { Pointer } from "rfc6902";
import { CollapseRow, Control211 } from "../controls";
import { useBeFigure } from "./BeFigureContextProvider";
import { FigureVars } from "./FigureVars";
import { ADD_PROPERTY, CHANGE_REGRESSION_LINE_TYPE } from "./functions/interface";
import { RemoveFigureItemButton } from "./RemoveFigureItemButton";
import { FigureVarData, isFigureVarData } from "./types/FigureVarData";
import { FigureVarSettings } from "./types/FigureVarSettings";

export interface FigureDataRowProps {
  id: string;
  dataPath: string;
  itemLabel: string;
  noNumber?: boolean;
  noRemove?: boolean;
  propertiesKey: string;
  sibLingCount: number;
  varSettings: FigureVarSettings[];
}

/**
 * Render a figure array item (data group, legend item, or regression line) using figure state context
 *
 * @param props - Destructured parameter object
 * @param props.id - The id for the item
 * @param props.dataPath - The path to the array in figureInput (e.g., "dataGroups", "/legend", "regressionLines")
 * @param props.itemLabel - The prefix for the item label (e.g., "Data series", "Legend item", "Regression line")
 * @param props.noNumber - Whether to show the number in the label (e.g., "Data series 1", "Data series 2", etc.)
 * @param props.noRemove - Whether to show the remove button
 * @param props.propertiesKey - The key for the properties array (e.g., "dataRows", "legendProperties", "lineProperties")
 * @param props.sibLingCount - The number of sibling items in the array
 * @param props.varSettings - Array of variable settings to display
 */
export const FigureDataRow = ({
  id,
  dataPath,
  itemLabel,
  noNumber = false,
  noRemove = false,
  propertiesKey,
  sibLingCount,
  varSettings,
}: FigureDataRowProps) => {
  const { state, dispatch } = useBeFigure();
  const figureSettings = state?.figureSettings;
  const figureInput = state?.figureInput;
  const [isOpen, setIsOpen] = useState(true);
  const figureTypeSettings = figureSettings?.figureTypeSettings.find(
    (fts) => fts.value === figureInput?.figureType,
  );
  const allowedRegressionLines: string[] = figureTypeSettings?.allowedRegressionTypes ?? [];

  // Get the array from figureInput using dataPath
  const pointer = Pointer.fromJSON(dataPath);
  const item = pointer.get(figureInput);
  const thisProps: FigureVarData[] = useMemo(
    () =>
      Array.isArray(item?.properties) && item.properties.every((p: unknown) => isFigureVarData(p))
        ? item.properties
        : [],
    [item],
  );
  const ix = parseInt(pointer.tokens[pointer.tokens.length - 1]);

  // Get the display label from the "name" property if it exists, otherwise use itemLabel N
  const displayLabel = useMemo(() => {
    const nameProperty = thisProps.find((p) => p.varName === "name");
    const labelProperty = thisProps.find((p) => p.varName === "label");
    if (nameProperty?.value) {
      return nameProperty.value;
    }
    if (labelProperty?.value) {
      return labelProperty.value;
    }
    return noNumber ? itemLabel : `${itemLabel} ${ix + 1}`;
  }, [thisProps, itemLabel, noNumber, ix]);

  // Check props exist for all varSettings, if not add empty
  const addMissingProps = useEffectEvent(
    (varSettings: FigureVarSettings[], thisProps: FigureVarData[]) =>
      varSettings.forEach((varSetting) => {
        const pix = thisProps.findIndex((p) => p.varName === varSetting.varName);
        if (pix < 0) {
          dispatch({
            operation: ADD_PROPERTY,
            pointer: `${dataPath}/properties`,
            varName: varSetting.varName,
          });
        }
      }),
  );
  useEffect(() => {
    addMissingProps(varSettings, thisProps);
  }, [varSettings, thisProps]);

  return !figureSettings ||
    !figureInput ||
    !item ||
    !thisProps ||
    varSettings.length === 0 ? null : (
    <CollapseRow
      className="border rounded border-gray-500 border-2 px-1 mb-2"
      label={
        <span className="fw-bold d-flex align-items-center">
          {displayLabel}
          {!noRemove && (
            <RemoveFigureItemButton
              id={`${id}-remove-btn`}
              dataPath={dataPath}
              sibLingCount={sibLingCount}
            />
          )}
        </span>
      }
      isOpen={isOpen}
      setIsOpen={() => setIsOpen(!isOpen)}
    >
      {dataPath.startsWith("/regressionLines") && item.lineType && (
        <Control211
          id={`${id}-line-type`}
          label="Regression line type"
          labelWidth={3}
          value={item.lineType}
          options={allowedRegressionLines}
          setValue={(newValue) => {
            dispatch({
              operation: CHANGE_REGRESSION_LINE_TYPE,
              pointer: `${dataPath}`,
              lineType: newValue,
            });
          }}
        />
      )}
      <FigureVars
        id={id}
        dataPath={`${dataPath}/${propertiesKey}`}
        varSettings={varSettings}
        properties={thisProps}
      />
    </CollapseRow>
  );
};

FigureDataRow.displayName = "FigureDataRow";
